import { useEffect, useMemo, useRef, useState } from 'react'
import type { Product } from '@/services/products'
import {
  collectProductImageCandidates,
  preloadProductImageUrl,
  resolveProductPrimaryImage,
  type StorefrontImageSource,
} from '@/storefront/lib/productImageResolve'

/**
 * Stable hero image: persistent resolve (localStorage/LRU) + hold previous URL while the next preloads
 * so the card does not flash empty or swap early to a broken asset.
 */
export function useStorefrontProductImage(
  product: Product | null | undefined,
  opts?: { blockImage?: string }
): { displayUrl: string; source: StorefrontImageSource; resolvedUrl: string } {
  const resolved = useMemo(() => {
    if (!product) return { url: '', source: 'none' as const }
    return resolveProductPrimaryImage(product, opts)
  }, [product, opts?.blockImage])

  const pid = product?.id ?? ''
  const stableMapRef = useRef<Map<string, string>>(new Map())
  const [gen, setGen] = useState(0)

  useEffect(() => {
    const url = resolved.url
    if (!pid) return
    if (!url) {
      setGen((g) => g + 1)
      return
    }
    if (stableMapRef.current.get(pid) === url) return
    let dead = false
    void preloadProductImageUrl(url).then(() => {
      if (dead) return
      stableMapRef.current.set(pid, url)
      setGen((g) => g + 1)
    })
    return () => {
      dead = true
    }
  }, [pid, resolved.url])

  const displayUrl = useMemo(() => {
    if (!pid) return ''
    const url = resolved.url
    const prev = stableMapRef.current.get(pid)
    if (!url) return prev ?? ''
    if (!prev || prev === url) return url
    return prev
  }, [pid, resolved.url, gen])

  const source: StorefrontImageSource = useMemo(() => {
    if (!displayUrl) return 'none'
    if (displayUrl === resolved.url) return resolved.source
    return 'cache'
  }, [displayUrl, resolved.url, resolved.source])

  return { displayUrl, source, resolvedUrl: resolved.url }
}

export function useSecondaryProductImageUrl(product: Product, primaryDisplay: string): string {
  return useMemo(() => {
    const urls = collectProductImageCandidates(product).map((c) => c.url)
    const second = urls.find((u) => u && u !== primaryDisplay)
    return second && second !== primaryDisplay ? second : ''
  }, [product, primaryDisplay])
}
